// ABC SEO/GEO — LIVE Search Performance. Real Google Search Console data
// pulled from the deployed Worker API (not sample data).
(function () {
  const { Icon } = window.SGIcons;
  const { Card, CardHead, PageHead, Delta } = window.SGKit;

  function Stat({ label, value, sub, accent }) {
    return (
      <Card style={{ padding: 18 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--sg-text-2)', marginBottom: 10 }}>{label}</div>
        <div className="sg-tnum" style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 32,
          letterSpacing: '-0.02em', color: accent || 'var(--sg-text)', lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--sg-text-3)', marginTop: 8 }}>{sub}</div>}
      </Card>
    );
  }

  function MiniBar({ value, max, color }) {
    return (
      <div style={{ height: 7, borderRadius: 999, background: 'var(--sg-sunken)', overflow: 'hidden', minWidth: 60 }}>
        <div style={{ width: (max ? (value / max) * 100 : 0) + '%', height: '100%', borderRadius: 999, background: color }} />
      </div>
    );
  }

  function SearchPerformance() {
    const { sites, site, setSite, perf, err, loading, prettySite } = window.SGLive.useLive();

    // "My keywords only" — filter Top queries to the client's approved keyword set (default ON).
    const [myOnly, setMyOnly] = React.useState(true);
    const [approved, setApproved] = React.useState(null);
    React.useEffect(() => { window.SGLive.fetchApprovedKeywords().then(setApproved).catch(() => setApproved([])); }, []);
    const approvedSet = React.useMemo(() => new Set((approved || []).map((k) => k.toLowerCase())), [approved]);
    const hasApproved = approvedSet.size > 0;
    const filterOn = myOnly && hasApproved;

    const queries = perf
      ? (filterOn ? perf.byQuery.filter((r) => approvedSet.has(((r.keys && r.keys[0]) || '').toLowerCase())) : perf.byQuery)
      : [];
    const maxClicks = queries.length ? Math.max(1, ...queries.map((r) => r.clicks)) : 1;

    const seg = (active) => ({ display: 'inline-flex', alignItems: 'center', height: 30, padding: '0 12px', border: 'none', cursor: 'pointer',
      fontFamily: 'var(--sg-font-sans)', fontSize: 12, fontWeight: 600, borderRadius: 'var(--sg-radius-pill)',
      color: active ? 'var(--sg-text)' : 'var(--sg-text-2)', background: active ? 'var(--sg-card)' : 'transparent',
      boxShadow: active ? 'var(--sg-shadow)' : 'none' });

    return (
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <PageHead
          eyebrow="● LIVE · GOOGLE SEARCH CONSOLE" eyebrowColor="var(--sg-green)"
          title="Search Performance" icon="trending-up"
          sub="Your real Google search data — actual clicks, impressions, and positions — pulled live from the connected account. This is ground truth, not estimates."
          right={
            site
              ? <span title="Switch domains from the picker at the top of the page"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-sunken)', fontSize: 13, fontWeight: 600, color: 'var(--sg-text-2)' }}>
                  <Icon name="globe" size={14} /> {prettySite(site)}
                </span>
              : null
          }
        />

        {err && <Card style={{ padding: 16, color: 'var(--sg-red)' }}>Couldn't load live data: {err}</Card>}
        {loading && <Card style={{ padding: 40, textAlign: 'center', color: 'var(--sg-text-2)' }}>Fetching live data from Google…</Card>}

        {perf && (
          <React.Fragment>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
              <Stat label="Total clicks" value={perf.totals.clicks.toLocaleString()} sub="last 28 days" accent="var(--sg-blue-600)" />
              <Stat label="Total impressions" value={perf.totals.impressions.toLocaleString()} sub="last 28 days" />
              <Stat label="Average CTR" value={(perf.totals.ctr * 100).toFixed(1) + '%'} sub="clicks ÷ impressions" />
              <Stat label="Average position" value={perf.totals.position.toFixed(1)} sub="lower is better" accent="var(--sg-violet-700)" />
            </div>

            <Card>
              <CardHead title="Top queries" sub={'Real searches that surfaced ' + prettySite(site)}
                right={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {hasApproved && (
                      <div style={{ display: 'inline-flex', background: 'var(--sg-sunken)', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', padding: 3 }}>
                        <button onClick={() => setMyOnly(true)} style={seg(myOnly)}>My keywords</button>
                        <button onClick={() => setMyOnly(false)} style={seg(!myOnly)}>All queries</button>
                      </div>
                    )}
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>
                  </div>
                } />
              {filterOn && (
                <div style={{ padding: '8px 20px', fontSize: 12, color: 'var(--sg-text-3)', borderBottom: '1px solid var(--sg-border)' }}>
                  Showing your approved keywords · <button onClick={() => setMyOnly(false)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--sg-blue-600)', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>show all</button>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 90px 80px 70px', gap: 14, padding: '10px 20px',
                borderBottom: '1px solid var(--sg-border)', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'var(--sg-text-3)' }}>
                <span>Query</span><span>Clicks</span><span>Impr.</span><span>CTR</span><span>Pos.</span>
              </div>
              {queries.length === 0
                ? (
                  <div style={{ padding: '22px 20px', fontSize: 13, color: 'var(--sg-text-2)' }}>
                    {filterOn
                      ? 'None of your approved keywords have Search Console impressions yet — switch to “All queries” to see every search that surfaced your site.'
                      : 'No query data for this site yet.'}
                  </div>
                )
                : queries.slice(0, 15).map((r, i, arr) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 90px 80px 70px', gap: 14, padding: '11px 20px',
                    alignItems: 'center', borderBottom: i < arr.slice(0, 15).length - 1 ? '1px solid var(--sg-border)' : 'none' }}>
                    <span style={{ fontSize: 13.5, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.keys[0]}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <MiniBar value={r.clicks} max={maxClicks} color="var(--sg-grad-brand)" />
                      <span className="sg-tnum" style={{ fontSize: 13, fontWeight: 700, width: 22, textAlign: 'right' }}>{r.clicks}</span>
                    </div>
                    <span className="sg-tnum" style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>{r.impressions.toLocaleString()}</span>
                    <span className="sg-tnum" style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>{(r.ctr * 100).toFixed(1)}%</span>
                    <span className="sg-tnum" style={{ fontSize: 13, fontWeight: 700, color: 'var(--sg-text)' }}>{r.position.toFixed(1)}</span>
                  </div>
                ))}
            </Card>
          </React.Fragment>
        )}
      </div>
    );
  }

  window.SGSearchPerformance = SearchPerformance;
})();
